import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { AccountConfigurationService } from '../../services/account-configuration.service';
import { UserEmail } from '../../models/interfaces/user-email-input.model';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { EventEmitter } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MessageDialogModalDialog } from '../errors-dialog/message-dialog-modal';
import { ProjectsVisibilityGroupPostBody } from '../../models/interfaces/projects-visibility-group-post-body.model';
import { NotificationService } from '../../services/notification-service';
import DialogDataDto from '../../models/new-file-dialog-dto';
import { AccountContextService } from 'src/app/authentication/services/account-context.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-project-visibility-dialog',
  templateUrl: './project-visibility-dialog.component.html',
  styleUrls: ['./project-visibility-dialog.component.scss']
})
export class ProjectVisibilityDialogComponent implements OnInit, OnDestroy {

  saveCompleted = new EventEmitter<void>();
  user: UserEmail = null;
  enableSaveButton: boolean = false;
  userList: { email: string, userId: string, invalid?: boolean }[] = [];
  allEmailsInGroups: string[] = [];
  displayColumn: string[] = ['email', 'remove'];
  emailFormControl = new UntypedFormControl('', [
    Validators.required,
    Validators.email,
  ]);
  groupName = new UntypedFormControl('', [
    Validators.required,
  ]);
  accountUserList: UserEmail[] = [];
  addButtonDisabled: boolean = false;
  private destroy$ = new Subject<void>();

  @ViewChild(MatTable) table: MatTable<{ email: string }>;

  constructor(
    private accountConfigurationService: AccountConfigurationService,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<ProjectVisibilityDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogDataDto,
    private notificationService: NotificationService,
    private accountContext: AccountContextService) { }

  ngOnInit(): void {
    const account = this.accountContext.getSelectedAccountId();

    if (!account) {
      return;
    }

    this.accountUserList = JSON.parse(sessionStorage.getItem('accountUserList') || '[]');

    if (this.accountUserList.length == 0) {
      this.accountConfigurationService.getAccountUsers(account)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
        next: (data) => {
          this.accountUserList = data.map(c => ({ email: c.Email, userID: `${c.UserID}` }));
          sessionStorage.setItem('accountUserList', JSON.stringify(this.accountUserList));
          this.showGroupUsers();
        },
        error: (error) => {
          console.error(error);
        }
      });
    }

    this.showGroupUsers();
    this.allEmailsInGroups = this.data.emailList;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showGroupUsers() {
    if (this.data.field) {
      let groupUserIdList = this.data.field.Users;
      let accountUserInGroupList = this.accountUserList.filter(f => groupUserIdList.includes(f.userID))
      this.userList = accountUserInGroupList.map(user => ({ userId: user.userID, email: user.email }));
      this.groupName.setValue(this.data.field.Name);
    }
  }

  addEmailToList() {
    if (this.emailFormControl.errors) {
      return;
    }
    
    const enteredEmail = this.emailFormControl.value;
    
    const isEmailInList = this.allEmailsInGroups.includes(enteredEmail);
    
    if (isEmailInList) {
      this.emailFormControl.setErrors({ emailAlreadyInList: true });
      this.dialog.open(MessageDialogModalDialog, {
        data: {
          title: "visibility.messages.alertTitle",
          message: "visibilityRequest.errors.emailAlreadyRegisteredInAnotherGroup",
          isError: true
        }
      });
      return;
    }
    
    const isAlreadyAddedInList = this.userList.map(c => c.email).includes(enteredEmail);
    
    if (isAlreadyAddedInList) {
      this.emailFormControl.setErrors({ emailAlreadyInList: true });
      this.dialog.open(MessageDialogModalDialog, {
        data: {
          title: "visibility.messages.alertTitle",
          message: "visibilityRequest.errors.emailAlreadyRegisteredInGroup",
          isError: true
        }
      });
      return;
    }
    
    this.addButtonDisabled = true;

    this.accountConfigurationService.getUserByEmailAsync(enteredEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
      this.user = user;
      this.emailFormControl.setErrors(user == null ? { invalidUser: true } : null);

      if (this.emailFormControl.valid) {
        this.userList.push({ email: enteredEmail, userId: user.userID });
        this.emailFormControl.setValue('', { emitEvent: false });
        this.emailFormControl.setErrors(null, { emitEvent: false });
        this.emailFormControl.markAsPristine();
        this.emailFormControl.markAsUntouched();
        this.table.renderRows();
      }

      this.addButtonDisabled = false;
    });
  }

  removeEmail(email: string) {
    const index = this.userList.findIndex(item => item.email === email);
    if (index !== -1) {
      this.userList.splice(index, 1);
      this.table.renderRows();
    }
  }

  onSave(): void {
    if (!this.groupName.value || this.userList.length === 0) {
      this.showErrorMessage('visibility.messages.errorFieldEmailAndName');
    } else {
      const groupRequest: ProjectsVisibilityGroupPostBody = {
        Name: this.groupName.value,
        Users: this.userList.map(item => item.userId),
      };

      const account = this.accountContext.getSelectedAccountId();

      if (!account) {
        return;
      }

      this.emailFormControl.markAsPending();

      if (this.data.field) {
        this.accountConfigurationService.PatchProjectsVisibilityGroup(account, this.data.field.Id, groupRequest)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.dialog.open(MessageDialogModalDialog, {
                data: {
                  title: "",
                  message: "visibilityRequest.success.groupSuccessfullyModified",
                  isError: false
                }
              });
              this.notificationService.notify.emit();
              this.dialogRef.close();
            },
            error: (error) => {
              if (error.status === 500) {
                this.dialog.open(MessageDialogModalDialog, {
                  data: {
                    title: "visibility.messages.alertTitle",
                    message: "visibilityRequest.errors.internalServerError",
                    isError: false
                  }
                });
              } else if (error.status === 400) {
                this.dialog.open(MessageDialogModalDialog, {
                  data: {
                    title: "visibility.messages.alertTitle",
                    message: "visibilityRequest.errors.emailAlreadyRegisteredInAnotherGroup",
                    isError: true
                  }
                });

                this.setInvalidEmailsOnList(error.error);
              }
            }
          });
      } else {
        this.accountConfigurationService.postProjectsVisibilityGroup(account, groupRequest)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.dialog.open(MessageDialogModalDialog, {
                data: {
                  title: "",
                  message: "visibilityRequest.success.groupRegisteredSuccessfully",
                  isError: false
                }
              });
              this.notificationService.notify.emit();
              this.dialogRef.close();
            },
            error: (error) => {
              if (error.status === 500) {
                this.dialog.open(MessageDialogModalDialog, {
                  data: {
                    title: "visibility.messages.alertTitle",
                    message: "visibilityRequest.errors.internalServerError",
                    isError: false
                  }
                });
              } else if (error.status === 400) {
                this.dialog.open(MessageDialogModalDialog, {
                  data: {
                    title: "visibility.messages.alertTitle",
                    message: "visibilityRequest.errors.emailAlreadyRegisteredInAnotherGroup",
                    isError: true
                  }
                });

                this.setInvalidEmailsOnList(error.error);
              }
            }
          });
        }
    }
  }

  showErrorMessage(message: string): void {
    this.dialog.open(MessageDialogModalDialog, {
      data: {
        title: "",
        message,
        isError: true
      },
    });
  }
  onCloseClick(): void {
    this.dialogRef.close();
  }

  setInvalidEmailsOnList(errorMessage: string) {
    const userIdListOnMessage = errorMessage.split(" ")[1];
    const userIdList = userIdListOnMessage.split(",");
    const userListOfInvalidUsers = this.userList.filter(x => userIdList.includes(x.userId))

    userListOfInvalidUsers.forEach(u => u.invalid = true)
  }
}
